import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	OverrideContainerComponent,
} from 'react-sweet-state';
import { createActionsHook, createContainer, createHook, createStore } from 'react-sweet-state';

import type { ThemeColorModes, ThemeState } from '@atlaskit/tokens';

import { addIframeThemingSupport } from '@confluence/theming-utils/entry-points/initializeIframeTheming.ts';
import { getSystemPreferredColorScheme } from '@confluence/theming-utils/entry-points/systemColorScheme.ts';
import { applyTheme } from '@confluence/theming-utils/entry-points/applyTheme.ts';
import { getDefaultTheme } from '@confluence/theming-utils/entry-points/defaultTheme.ts';
import { buildThemeFromState } from '@confluence/theming-utils/entry-points/buildTheme.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';

import { ErrorSubjects, ErrorSubjectIds, reportThemingError } from './errorHandling.ts';
import { ThemeAPI } from './themeAPI.ts';

export interface ConfluenceThemeState {
	isInitialized: boolean;
	theme: Partial<ThemeState>;
}

const initialState: ConfluenceThemeState = {
	isInitialized: false,
	theme: getDefaultTheme(),
};

type ThemingStateContainerProps = {
	initialTheme?: Partial<ThemeState>;
};

export const themingActions = {
	applyTheme: (theme: Partial<ThemeState>) => async (): Promise<void> => {
		await applyTheme(theme);
		const client = await getAnalyticsWebClient();
		client.appendUIViewedAttributes({
			theme,
			// OStheme may become stale until the session is restarted since the user can
			//      change their system/browser settings at any time without manually changing their theme in the app.
			OStheme: getSystemPreferredColorScheme(),
		});
	},
	initializeTheme:
		(initialTheme?: Partial<ThemeState>) =>
		async ({ setState, dispatch }: { setState: any; dispatch: any }): Promise<void> => {
			if (!initialTheme) {
				// Get local theme first to help render theme faster than waiting to fetch latest preference from remote
				const persistedLocalTheme = ThemeAPI.getSavedThemeLocal();
				if (persistedLocalTheme) {
					dispatch(themingActions.applyTheme(buildThemeFromState(persistedLocalTheme)));
				}
			}

			addIframeThemingSupport();

			const theme = initialTheme || (await ThemeAPI.getSavedTheme());

			// Check if it's the PDF export route and force light mode if it is
			const isPdfExport = PDF_EXPORT_PAGE.match(window.location.pathname);
			if (isPdfExport) {
				theme.colorMode = 'light';
			}

			const effectiveTheme = buildThemeFromState(theme);
			setState({
				isInitialized: true,
				theme: effectiveTheme,
			});
			await dispatch(themingActions.applyTheme(effectiveTheme));
		},
	setColorModePreference:
		(themeColorMode: ThemeColorModes) =>
		async ({
			getState,
			setState,
			dispatch,
		}: {
			getState: any;
			setState: any;
			dispatch: any;
		}): Promise<void> => {
			const theme = {
				...getState().theme,
				colorMode: themeColorMode,
			};
			try {
				setState({
					...getState(),
					theme,
				});
				await Promise.all([dispatch(themingActions.applyTheme(theme)), ThemeAPI.saveTheme(theme)]);
			} catch (e) {
				const attributes = {
					message: e.message,
					stack: e.stack,
				};
				await reportThemingError(
					ErrorSubjects.useThemingState,
					ErrorSubjectIds.setColorMode,
					attributes,
				);
			}
		},
	restoreThemePreference:
		() =>
		async ({ dispatch, getState }: { dispatch: any; getState: any }): Promise<void> => {
			const { theme } = getState();
			await dispatch(themingActions.applyTheme(theme));
		},
};

export type ThemingActions = typeof themingActions;

const Store = createStore<ConfluenceThemeState, ThemingActions>({
	initialState,
	actions: themingActions,
	name: 'themingState',
});

// Used for testing
export const ThemingStateContainer: OverrideContainerComponent<ThemingStateContainerProps> =
	createContainer<ConfluenceThemeState, ThemingActions, ThemingStateContainerProps>(Store, {
		onInit:
			() =>
			async ({ dispatch }, { initialTheme }) => {
				void dispatch(themingActions.initializeTheme(initialTheme));
			},
	});
export const useThemingState: HookFunction<
	ConfluenceThemeState,
	BoundActions<
		ConfluenceThemeState,
		{
			applyTheme: (theme: Partial<ThemeState>) => () => Promise<void>;
			initializeTheme: (
				initialTheme?: Partial<ThemeState>,
			) => ({ setState, dispatch }: { setState: any; dispatch: any }) => Promise<void>;
			setColorModePreference: (
				themeColorMode: ThemeColorModes,
			) => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => Promise<void>;
			restoreThemePreference: () => ({
				dispatch,
				getState,
			}: {
				dispatch: any;
				getState: any;
			}) => Promise<void>;
		}
	>,
	void
> = createHook<ConfluenceThemeState, ThemingActions>(Store);

// This hook is used just for consumers that only need to dispatch actions but don't
//    rely on the state and prevents re-renders in those consumers
export const useThemingActions: HookActionsFunction<
	BoundActions<
		ConfluenceThemeState,
		{
			applyTheme: (theme: Partial<ThemeState>) => () => Promise<void>;
			initializeTheme: (
				initialTheme?: Partial<ThemeState>,
			) => ({ setState, dispatch }: { setState: any; dispatch: any }) => Promise<void>;
			setColorModePreference: (
				themeColorMode: ThemeColorModes,
			) => ({
				getState,
				setState,
				dispatch,
			}: {
				getState: any;
				setState: any;
				dispatch: any;
			}) => Promise<void>;
			restoreThemePreference: () => ({
				dispatch,
				getState,
			}: {
				dispatch: any;
				getState: any;
			}) => Promise<void>;
		}
	>
> = createActionsHook<ConfluenceThemeState, ThemingActions>(Store);
